import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { Button, Panel, Toggle, colors } from 'src/components/app-ui'
import UpgradePopover from 'src/components/upgrade-popover/upgrade-popover'
import { useCourseSettings } from 'src/hooks/useCourseSettings'
import {
  CardPublishState,
  useCoursePublish,
} from 'src/contexts/course-publish-context'
import { editorPath } from '../tabs'
import { GENERAL_SETTING_KEY } from '../settings-tab/use-general-settings'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  sequential: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    padding: '14px 20px',
  },
  sequentialText: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  primaryIcon: {
    width: 18,
    height: 18,
    flex: 'none',
    color: colors.primary,
  },
  title: {
    fontSize: '14px',
    fontWeight: 500,
  },
  desc: {
    fontSize: '13px',
    color: colors.muted,
  },
  list: {
    overflow: 'hidden',
  },
  listHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    padding: '14px 16px 14px 20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 20px',
    borderBottom: '1px solid #F1F3F5',
    cursor: 'pointer',
    '&:hover': {
      background: '#F7F7F7',
    },
  },
  step: {
    width: 26,
    height: 26,
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1.5px solid #D0D5D9',
    borderRadius: '50%',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.muted,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
  rowTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.navy,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMeta: {
    fontSize: '12px',
    color: colors.subtle,
    whiteSpace: 'nowrap',
  },
  draft: {
    padding: '3px 10px',
    borderRadius: '999px',
    background: '#FEF3DD',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.warningDark,
    whiteSpace: 'nowrap',
  },
  chevron: {
    display: 'inline-flex',
    color: colors.disabled,
  },
  empty: {
    padding: '56px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    textAlign: 'center',
  },
  emptyIcon: {
    width: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    background: colors.primarySoft,
    color: colors.primary,
  },
  emptyTitle: {
    fontSize: '16px',
    fontWeight: 600,
  },
  emptyDesc: {
    maxWidth: 420,
    marginTop: 4,
    fontSize: '14px',
    lineHeight: 1.5,
    color: colors.muted,
  },
})

/** Pages of a section that learners can't see yet (never published). */
const countDraftPages = (section, pages) =>
  section.cards.filter(card => {
    const page = pages.find(p => String(p.cardId) === String(card.id))
    return page && page.state === CardPublishState.DRAFT
  }).length

/** Sequential progression lives in the course's `general` settings JSON. */
const useSequentialSections = () => {
  const { value, exists, execute, update } = useCourseSettings(
    GENERAL_SETTING_KEY,
  )
  const stored = Boolean(value?.isSequential)
  const [isSequential, setIsSequential] = useState(stored)

  useEffect(() => {
    setIsSequential(stored)
  }, [stored])

  const setSequential = next => {
    setIsSequential(next)
    const nextValue = { ...(value || {}), isSequential: next }
    if (exists) update(nextValue)
    else execute(nextValue)
  }

  return [isSequential, setSequential]
}

const SectionsTab = ({ course, sections }) => {
  const classes = useStyles()
  const history = useHistory()
  const publishState = useCoursePublish()
  const pages = publishState?.pages || []
  // A draft course is all draft: per-section draft counts only matter once
  // it is live (pages edited since the last publish).
  const showDrafts = Boolean(publishState?.isLoaded && publishState.isPublished)
  const [isSequential, setSequential] = useSequentialSections()
  const [upgradeAnchor, setUpgradeAnchor] = useState(null)
  const isPremium = Number(window.CONSTANTS.USER_ROLE_ID) !== 1

  const openEditor = cardId => history.push(editorPath(course.id, cardId))

  if (sections.length === 0) {
    return (
      <Panel className={classes.empty}>
        <span className={classes.emptyIcon}>
          <Icon icon="tabler:file-plus" width={22} height={22} />
        </span>
        <div>
          <div className={classes.emptyTitle}>This course is empty</div>
          <div className={classes.emptyDesc}>
            Open the editor to add your first section and pages.
          </div>
        </div>
        <Button icon="tabler:pencil" onClick={() => openEditor()}>
          Open editor
        </Button>
      </Panel>
    )
  }

  const handleSequential = (next, event) => {
    if (!isPremium) {
      setUpgradeAnchor(event.currentTarget)
      return
    }
    setSequential(next)
  }

  return (
    <div className={classes.root}>
      <Panel className={classes.sequential}>
        <div className={classes.sequentialText}>
          <Icon icon="tabler:arrows-sort" className={classes.primaryIcon} />
          <div>
            <div className={classes.title}>Sequential pages</div>
            <div className={classes.desc}>
              Learners can't proceed unless previous pages were viewed.
            </div>
          </div>
        </div>
        <Toggle checked={isSequential} onChange={handleSequential} />
      </Panel>

      <Panel className={classes.list}>
        <div className={classes.listHead}>
          <span className={classes.desc}>
            Add, reorder and edit sections and pages in the editor.
          </span>
          <Button
            size="small"
            icon="tabler:pencil"
            onClick={() => openEditor()}
          >
            Open editor
          </Button>
        </div>
        {sections.map((section, index) => {
          const pageCount = section.cards.length
          const drafts = countDraftPages(section, pages)
          return (
            <div
              key={section.id}
              className={classes.row}
              title="Open in editor"
              onClick={() => openEditor(section.cards[0]?.id)}
            >
              <span className={classes.step}>{index + 1}</span>
              <div className={classes.rowText}>
                <span className={classes.rowTitle}>
                  {section.name || `Section ${index + 1}`}
                </span>
                <span className={classes.rowMeta}>
                  {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                </span>
              </div>
              {showDrafts && drafts > 0 && (
                <span className={classes.draft}>
                  {drafts} {drafts === 1 ? 'draft page' : 'draft pages'}
                </span>
              )}
              <span className={classes.chevron}>
                <Icon icon="tabler:chevron-right" width={16} height={16} />
              </span>
            </div>
          )
        })}
      </Panel>

      <UpgradePopover
        id="sequential-popover"
        open={Boolean(upgradeAnchor)}
        anchorEl={upgradeAnchor}
        handleClose={() => setUpgradeAnchor(null)}
        header="Upgrade to Plus"
        content="Upgrade to Plus plan to access this feature."
      />
    </div>
  )
}

export default SectionsTab
